import userStore from "@/store/modules/userStore";
import { reactive, readonly } from "vue";
import { EnumIntegrationType } from "@/app/services/generated/enums/enumIntegrationType.generated";
import type { BackgroundProcessingErrorKey } from "@/components/pages/integrations/composables/backgroundProcessing";
import { getBackgroundProcessingErrors } from "@/components/pages/integrations/composables/backgroundProcessing";
import { groupBy } from "lodash-es";
import { ecommerceConnectionSettingsService } from "@/app/services/generated/ecommerceconnectionsettingsService";
import { eposNowSettingsService } from "@/app/services/generated/eposnowsettingsv2Service";
import { squareSettingsService } from "@/app/services/generated/squaresettingsService";
import { wooCommerceSettingsService } from "@/app/services/generated/woocommercesettingsService";
import {
  getCommerce7LinkStatus,
  getCommerce7Orders,
} from "@/components/pages/integrations/commerce7/integrationsCommerce7";
import { sellarSettingsService } from "@/app/services/generated/sellarsettingsService";
import {
  getBigCommerceLinkStatus,
  getBigCommerceOrders,
} from "@/components/pages/integrations/bigCommerce/integrationsBigCommerce";
import {
  getBeerflexOrders,
  beerflexIsActivated,
} from "@/components/pages/integrations/composables/integrationsBeerflex";
import {
  ecwidMeta,
  getEcwidOrders,
} from "@/components/pages/integrations/composables/integrationsEcwid";
import {
  getEebriaOrders,
  useEebriaIntegration,
} from "@/components/pages/integrations/composables/integrationsEebria";
import {
  getSquarespaceOrders,
  useSquarespaceIntegration,
} from "@/components/pages/integrations/composables/integrationsSquarespace";
import {
  getZettleOrders,
  zettleIsActivated,
} from "@/components/pages/integrations/composables/integrationsZettle";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { Instant, LocalDate } from "@js-joda/core";

import {
  EposNowOrderFilter,
  eposNowOrderService,
} from "@/app/services/generated/eposnoworderv2Service";

import {
  EcommerceOrderFilter,
  ecommerceOrderService,
} from "@/app/services/generated/ecommerceorderService";
import {
  SquareOrderFilter,
  squareOrderService,
} from "@/app/services/generated/squareorderService";
import {
  WooCommerceOrderFilter,
  wooCommerceOrderService,
} from "@/app/services/generated/woocommerceorderService";
import { getSellarOrders } from "@/components/pages/integrations/composables/integrationsSellar";
import { isNotNullOrUndefined } from "@/app/apiutils";
import { beerflexSettingsService } from "@/app/services/generated/beerflexsettingsService";
import { ecwidSettingsService } from "@/app/services/generated/ecwidsettingsService";
import { zettleSettingsService } from "@/app/services/generated/zettlesettingsService";

export type IntegrationError = {
  integrationType: EnumIntegrationType;
  error: string;
  timestamp: Instant;
};

export function fillFilterDates<
  TFilter extends {
    createdFromDate: LocalDate | null;
    createdToDate: LocalDate | null;
  },
>(filter: TFilter, fromDate?: LocalDate | null, toDate?: LocalDate | null) {
  filter.createdFromDate = fromDate ?? null;
  filter.createdToDate = toDate ?? null;
  return filter;
}

export async function getEposNowOrders(
  fromDate?: LocalDate,
  toDate?: LocalDate
) {
  return (
    await eposNowOrderService.getEposNowOrdersByFilter(
      fillFilterDates(new EposNowOrderFilter(), fromDate, toDate)
    )
  ).eposNowOrders;
}

export async function getEcommerceOrders(
  fromDate?: LocalDate,
  toDate?: LocalDate,
  originStoreIdentifiers?: string[]
) {
  const filter = fillFilterDates(new EcommerceOrderFilter(), fromDate, toDate);
  if (originStoreIdentifiers) {
    filter.ecommerceStoreIdentifiers = originStoreIdentifiers;
  }

  return (await ecommerceOrderService.getEcommerceOrdersByFilter(filter))
    .ecommerceOrders;
}

export async function getSquareOrders(
  fromDate?: LocalDate,
  toDate?: LocalDate,
  squareMerchantIdentifiers?: string[]
) {
  const filter = fillFilterDates(new SquareOrderFilter(), fromDate, toDate);
  if (squareMerchantIdentifiers) {
    filter.squareMerchantIdentifiers = squareMerchantIdentifiers;
  }

  return (await squareOrderService.getSquareOrdersByFilter(filter))
    .squareOrders;
}

export async function getWoocommerceOrders(
  fromDate?: LocalDate,
  toDate?: LocalDate,
  storeUrls?: string[]
) {
  const filter = fillFilterDates(
    new WooCommerceOrderFilter(),
    fromDate,
    toDate
  );
  if (storeUrls) {
    filter.storeUrls = storeUrls;
  }

  return (await wooCommerceOrderService.getWooCommerceOrdersByFilter(filter))
    .wooCommerceOrders;
}

function getLatestError(importErrors: IntegrationError[]) {
  return importErrors
    .filter((x) => x.error.length > 0)
    .sort((a, b) => b.timestamp.toEpochMilli() - a.timestamp.toEpochMilli())[0];
}

async function getLatestImportErrors(
  activeIntegrations: BackgroundProcessingErrorKey[],
  secondsAgo?: number
) {
  const importErrors: IntegrationError[] = [];

  const fromTimestamp = tenantService.now.minusSeconds(secondsAgo ?? 1800);
  const fromDate = tenantService.instantToDate(fromTimestamp);

  const activeIntegrationTypes = Array.from(
    new Set(activeIntegrations.map((x) => x.integrationType))
  );

  for (const activeIntegration of activeIntegrationTypes) {
    if (activeIntegration.isBeerflex()) {
      const results = await getBeerflexOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.beerflex,
            error: order.header.errorMessage,
            timestamp: order.header.importErroredAt!,
          }))
        )
      );
    } else if (activeIntegration.isBigCommerce()) {
      const results = await getBigCommerceOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.bigCommerce,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isCommerce7()) {
      const results = await getCommerce7Orders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.commerce7,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isEcwid()) {
      const results = await getEcwidOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.ecwid,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isEeBria()) {
      const results = await getEebriaOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.eeBria,
            error: order.errorMessage,
            timestamp: order.orderLastUpdatedAt,
          }))
        )
      );
    } else if (activeIntegration.isSquarespace()) {
      const results = await getSquarespaceOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.squarespace,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isZettle()) {
      const results = await getZettleOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.zettle,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isEposNow()) {
      const results = await getEposNowOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.eposNow,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isSellar()) {
      const results = await getSellarOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.sellar,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isIntegrateOnce()) {
      const results = await getEcommerceOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.integrateOnce,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isSquare()) {
      const results = await getSquareOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.square,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    } else if (activeIntegration.isWooCommerce()) {
      const results = await getWoocommerceOrders(fromDate);
      importErrors.push(
        getLatestError(
          results.map((order) => ({
            integrationType: EnumIntegrationType.wooCommerce,
            error: order.errorMessage,
            timestamp: order.createdAt,
          }))
        )
      );
    }
  }

  return importErrors.filter(isNotNullOrUndefined);
}

async function getActiveIntegrations() {
  const { eebriaMeta } = useEebriaIntegration();
  const { meta: squarespaceMeta } = useSquarespaceIntegration();

  const [
    beerflexSettings,
    bigCommerceLinkStatus,
    commerce7LinkStatus,
    allEcommerceSettings,
    allEposNowSettings,
    allSquareSettings,
    allWooCommerceSettings,
    allSellarSettings,
    ecwidSettings,
    zettleSettings,
  ] = await Promise.all([
    beerflexSettingsService.getBeerflexSettings(),
    getBigCommerceLinkStatus(),
    getCommerce7LinkStatus(),
    ecommerceConnectionSettingsService.getAllEcommerceConnectionSettings(),
    eposNowSettingsService.getAllEposNowSettings(),
    squareSettingsService.getAllSquareSettings(),
    wooCommerceSettingsService.getAllWooCommerceSettings(),
    sellarSettingsService.getAllSellarSettings(),
    ecwidSettingsService.getOrCreateEcwidSettings(),
    zettleSettingsService.getZettleSettings(),
  ]);

  const ecwidIsAuthenticated = await ecwidSettingsService.isEcwidAuthenticated(
    ecwidSettings.id
  );

  const integrationErrorKeys: BackgroundProcessingErrorKey[] = [];
  integrationErrorKeys.push(
    ...allEcommerceSettings
      .filter((x) => x.readOnly.status.isConnected())
      .map((settings) => ({
        integrationType: EnumIntegrationType.integrateOnce,
        contextId: settings.id,
      }))
  );
  integrationErrorKeys.push(
    ...allEposNowSettings
      .filter((x) => x.readOnly.apiKeyLength > 0)
      .map((settings) => ({
        integrationType: EnumIntegrationType.eposNow,
        contextId: settings.id,
      }))
  );
  integrationErrorKeys.push(
    ...allSquareSettings
      .filter((x) => x.outletId !== null || x.outletMappings.length)
      .map((settings) => ({
        integrationType: EnumIntegrationType.square,
        contextId: settings.id,
      }))
  );
  integrationErrorKeys.push(
    ...allWooCommerceSettings
      .filter(
        (x) =>
          x.outletId !== null &&
          x.readOnly.apiConsumerSecretLength !== 0 &&
          x.apiConsumerKey.length !== 0
      )
      .map((settings) => ({
        integrationType: EnumIntegrationType.wooCommerce,
        contextId: settings.id,
      }))
  );
  integrationErrorKeys.push(
    ...allSellarSettings
      .filter((x) => x.apiToken.length > 0)
      .map((settings) => ({
        integrationType: EnumIntegrationType.sellar,
        contextId: settings.id,
      }))
  );

  if (commerce7LinkStatus.status.isActive()) {
    integrationErrorKeys.push({
      integrationType: EnumIntegrationType.commerce7,
      contextId: null,
    });
  }

  if (bigCommerceLinkStatus.status.isActive()) {
    integrationErrorKeys.push({
      integrationType: EnumIntegrationType.bigCommerce,
      contextId: null,
    });
  }

  if (beerflexIsActivated(beerflexSettings)) {
    integrationErrorKeys.push({
      integrationType: EnumIntegrationType.beerflex,
      contextId: null,
    });
  }

  if (ecwidMeta(ecwidSettings, ecwidIsAuthenticated).activated) {
    integrationErrorKeys.push({
      integrationType: EnumIntegrationType.ecwid,
      contextId: null,
    });
  }

  if (eebriaMeta.value.activated) {
    integrationErrorKeys.push({
      integrationType: EnumIntegrationType.eeBria,
      contextId: null,
    });
  }

  if (squarespaceMeta.value.activated) {
    integrationErrorKeys.push({
      integrationType: EnumIntegrationType.squarespace,
      contextId: null,
    });
  }

  if (zettleIsActivated(zettleSettings)) {
    integrationErrorKeys.push({
      integrationType: EnumIntegrationType.zettle,
      contextId: null,
    });
  }

  return integrationErrorKeys;
}

export async function getLatestErrorsForAllActiveIntegrations() {
  const integrationErrorKeys = await getActiveIntegrations();
  const backgroundProcessingErrors = (
    await getBackgroundProcessingErrors(integrationErrorKeys, 7200)
  ).map(
    (x): IntegrationError => ({
      integrationType: x.integrationType,
      error: x.errorText,
      timestamp: x.timestamp,
    })
  );
  const importErrors = await getLatestImportErrors(
    integrationErrorKeys,
    604800
  );

  const allErrors = backgroundProcessingErrors.concat(importErrors);

  return Object.values(groupBy(allErrors, (x) => x.integrationType.key)).map(
    (g) =>
      g.sort(
        (error1, error2) =>
          error2.timestamp.toEpochMilli() - error1.timestamp.toEpochMilli()
      )[0]
  );
}

let timerId = 0;

interface IntegrationErrorStoreState {
  erroredIntegrationsCount: number;
  integrationErrors: IntegrationError[];
}

const state = reactive<IntegrationErrorStoreState>({
  erroredIntegrationsCount: 0,
  integrationErrors: [],
});

async function refresh() {
  if (
    !userStore.isAuthenticatedWithSelectedTenant.value ||
    !userStore.state.user
  ) {
    return;
  }

  try {
    const errors = await getLatestErrorsForAllActiveIntegrations();
    state.erroredIntegrationsCount = errors.length;
    state.integrationErrors = errors;
  } catch {
    // Ignore any error, leave the state as is.
  }
}

async function startAutoRefresh() {
  if (timerId) {
    clearInterval(timerId);
    timerId = 0;
    state.erroredIntegrationsCount = 0;
  }

  await refresh();

  timerId = +setInterval(refresh, 900000);
}

export default {
  state: readonly(state),
  startAutoRefresh,
  refresh,
};
